import { Component, OnInit } from '@angular/core';

interface MetricRow { key: string; value: number; }
interface CaseDemo {
  id: string;
  estado: string;
  fechaIngreso: string; // ISO
  fechaCierre?: string | null; // ISO
  entidad: string;
  region: string;
  compin?: string;
  tipoCierre?: string;
  diasPendientes?: number;
  dentroPlazo?: boolean;
  tieneApelacion?: boolean;
  apelacionConfirmada?: boolean;
  tiemposPorEtapa?: { [etapa: string]: number }; // days
}

@Component({
  selector: 'app-reportes-analiticas',
  templateUrl: './reportes-analiticas.component.html',
  styleUrls: ['./reportes-analiticas.component.scss']
})
export class ReportesAnaliticasComponent implements OnInit {
  public periodoDesde?: string;
  public periodoHasta?: string;
  public filtroRegion = '';
  public filtroCompin = '';
  public filtroInstitucion: any = '';
  public filtroEstado = '';
  public filtroTipoCierre = '';
  public filtroPendientesMasDe = 30; // dias

  // demo cases dataset
  private demoCases: CaseDemo[] = [];
  public filteredCases: CaseDemo[] = [];

  // computed KPIs
  public countsByEstado: { [k: string]: number } = {};
  public avgTimesByEtapa: { [k: string]: number } = {};
  public medianTimesByEtapa: { [k: string]: number } = {};
  public cumplimientoPorcentaje = 0;
  public pendientesPorEntidad: { entidad: string; count: number }[] = [];
  public apelacionesTotal = 0;
  public apelacionesConfirmadas = 0;

  // small demo chart
  public topReasons: Array<{ label: string; pct: number }> = [];
  public tableData: MetricRow[] = [];

  constructor() {}

  ngOnInit(): void {
    this.seedDemoCases();
    this.applyFilters();
  }

  private seedDemoCases() {
    // Create a small representative demo dataset
    this.demoCases = [
      { id: 'EXP-2025-0001', estado: 'en_comision', fechaIngreso: '2025-10-01', fechaCierre: '2025-11-10', entidad: 'Clínica Central', region: 'Metropolitana', compin: 'COMPIN RM', tipoCierre: 'administrativo', diasPendientes: 5, dentroPlazo: true, tieneApelacion: true, apelacionConfirmada: true, tiemposPorEtapa: { ingreso: 2, evaluacion: 10, comision: 20 } },
      { id: 'EXP-2025-0002', estado: 'en_revision', fechaIngreso: '2025-09-20', fechaCierre: null, entidad: 'Hospital Regional', region: 'Valparaíso', compin: 'COMPIN V', tipoCierre: '', diasPendientes: 45, dentroPlazo: false, tieneApelacion: false, apelacionConfirmada: false, tiemposPorEtapa: { ingreso: 1, evaluacion: 30 } },
      { id: 'EXP-2025-0003', estado: 'finalizado', fechaIngreso: '2025-08-10', fechaCierre: '2025-09-05', entidad: 'Centro Médico Sur', region: 'Biobío', compin: 'COMPIN B', tipoCierre: 'cierre', diasPendientes: 0, dentroPlazo: true, tieneApelacion: true, apelacionConfirmada: false, tiemposPorEtapa: { ingreso: 3, evaluacion: 20 } },
      // add more synthetic rows
      { id: 'EXP-2025-0004', estado: 'en_evaluacion', fechaIngreso: '2025-10-15', fechaCierre: null, entidad: 'Clínica Central', region: 'Metropolitana', compin: 'COMPIN RM', tipoCierre: '', diasPendientes: 60, dentroPlazo: false, tieneApelacion: false, apelacionConfirmada: false, tiemposPorEtapa: { ingreso: 2, evaluacion: 40 } },
      { id: 'EXP-2025-0005', estado: 'aprobado', fechaIngreso: '2025-07-01', fechaCierre: '2025-08-01', entidad: 'Hospital Regional', region: 'Valparaíso', compin: 'COMPIN V', tipoCierre: 'ejecutoriado', diasPendientes: 0, dentroPlazo: true, tieneApelacion: false, apelacionConfirmada: false, tiemposPorEtapa: { ingreso: 5, evaluacion: 10 } },
    ];
  }

  applyFilters() {
    // Filter demoCases according to the UI filters
    this.filteredCases = this.demoCases.filter(c => {
      if (this.filtroRegion && c.region !== this.filtroRegion) return false;
      if (this.filtroCompin && c.compin !== this.filtroCompin) return false;
      if (this.filtroInstitucion && c.entidad !== this.filtroInstitucion) return false;
      if (this.filtroEstado && c.estado !== this.filtroEstado) return false;
      if (this.filtroTipoCierre && c.tipoCierre !== this.filtroTipoCierre) return false;
      if (this.periodoDesde && new Date(c.fechaIngreso) < new Date(this.periodoDesde)) return false;
      if (this.periodoHasta && new Date(c.fechaIngreso) > new Date(this.periodoHasta)) return false;
      if (this.filtroPendientesMasDe && (c.diasPendientes ?? 0) <= this.filtroPendientesMasDe) return false;
      return true;
    });

    this.computeMetrics();
  }

  private computeMetrics() {
    const states: { [k: string]: number } = {};
    const tiemposByEtapa: { [k: string]: number[] } = {};
    let dentro = 0;
    let total = 0;
    let pendientesMap: { [ent: string]: number } = {};
    let apelTotal = 0;
    let apelConfirm = 0;

    for (const c of this.filteredCases) {
      states[c.estado] = (states[c.estado] || 0) + 1;
      if (c.tiemposPorEtapa) {
        for (const e of Object.keys(c.tiemposPorEtapa)) {
          tiemposByEtapa[e] = tiemposByEtapa[e] || [];
          tiemposByEtapa[e].push(c.tiemposPorEtapa![e]);
        }
      }
      if (c.dentroPlazo) dentro++;
      total++;
      if ((c.diasPendientes ?? 0) > (this.filtroPendientesMasDe ?? 0)) {
        pendientesMap[c.entidad] = (pendientesMap[c.entidad] || 0) + 1;
      }
      if (c.tieneApelacion) { apelTotal++; if (c.apelacionConfirmada) apelConfirm++; }
    }

    this.countsByEstado = states;
    // avg and median
    this.avgTimesByEtapa = {};
    this.medianTimesByEtapa = {};
    for (const k of Object.keys(tiemposByEtapa)) {
      const arr = tiemposByEtapa[k];
      const sum = arr.reduce((s, v) => s + v, 0);
      this.avgTimesByEtapa[k] = +(sum / arr.length || 0).toFixed(1);
      const sorted = arr.slice().sort((a,b)=>a-b);
      const mid = Math.floor(sorted.length/2);
      this.medianTimesByEtapa[k] = sorted.length %2 ===0 ? +( (sorted[mid-1]+sorted[mid])/2 ).toFixed(1) : +sorted[mid].toFixed(1);
    }

    this.cumplimientoPorcentaje = total? Math.round((dentro/total)*100) : 0;
    this.pendientesPorEntidad = Object.keys(pendientesMap).map(ent => ({ entidad: ent, count: pendientesMap[ent] })).sort((a,b)=>b.count-a.count);
    this.apelacionesTotal = apelTotal; this.apelacionesConfirmadas = apelConfirm;

    // fill topReasons and tableData for export/summary
    this.topReasons = [
      { label: 'Enfermedad crónica', pct: 40 },
      { label: 'Trauma laboral', pct: 25 },
      { label: 'Enfermedad aguda', pct: 20 },
      { label: 'Otros', pct: 15 },
    ];

    this.tableData = [
      { key: 'Casos filtrados', value: this.filteredCases.length },
      { key: 'Cumplimiento plazo (%)', value: this.cumplimientoPorcentaje },
      { key: 'Apelaciones (total)', value: this.apelacionesTotal },
      { key: 'Apelaciones confirmadas', value: this.apelacionesConfirmadas }
    ];
  }

  exportCSV() {
    // include filters in CSV header
    const header = `"Filtros: Desde:${this.periodoDesde||'-'};Hasta:${this.periodoHasta||'-'};Region:${this.filtroRegion||'-'}"\n`;
    const rows = [['Métrica','Valor'], ...this.tableData.map(r => [r.key, r.value])];
    const csv = header + rows.map(r => r.map(c => '"'+String(c).replace(/"/g,'""')+'"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); const url = URL.createObjectURL(blob);
    a.href = url; a.download = 'reportes-analiticas.csv'; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  exportExcel() {
    // Simple Excel-compatible CSV file with .xls extension for demo
    const rows = [['Métrica','Valor'], ...this.tableData.map(r => [r.key, r.value])];
    const csv = rows.map(r => r.map(c => '"'+String(c).replace(/"/g,'""')+'"').join(',')).join('\n');
    const blob = new Blob([csv], { type: 'application/vnd.ms-excel' });
    const a = document.createElement('a'); const url = URL.createObjectURL(blob);
    a.href = url; a.download = 'reportes-analiticas.xls'; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  }

  exportPDF() {
    // printable summary
    const win = window.open('', '_blank', 'noopener');
    if (!win) { alert('Permite popups para exportar PDF'); return; }
    const htmlRows = this.tableData.map(t => `<tr><td>${t.key}</td><td style="text-align:right">${t.value}</td></tr>`).join('');
    win.document.write(`<html><head><title>Reportes</title><style>table{width:100%;border-collapse:collapse}td,th{border:1px solid #ddd;padding:8px}</style></head><body><h2>Reportes y Analíticas</h2><div>Filtros: Desde ${this.periodoDesde||'-'} Hasta ${this.periodoHasta||'-'} Region ${this.filtroRegion||'-'}</div><table><thead><tr><th>Métrica</th><th>Valor</th></tr></thead><tbody>${htmlRows}</tbody></table></body></html>`);
    win.document.close(); win.focus(); setTimeout(() => win.print(), 300);
  }
}
